import { tokens, Field, Input, InputOnChangeData, Button, makeStyles, shorthands } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { CurrencyInput } from "../../EditorHelpers/CurrencyInput";
import { VATDropdown } from "../../EditorHelpers/VATDropdown";
import { useEditorStyles, TrashCanIcon } from "../../Styles/Editor";
import * as React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { InvoiceEntry2 } from "../InvoicesEditor";
import { CSS } from "@dnd-kit/utilities";

const useStyles = makeStyles({
    row:
    {
        display: "grid",
        gridTemplateColumns: "1fr 13fr 4fr 4fr 1fr",
        alignItems: "self-start",
        ...shorthands.gap("0.5rem"),
    },
});

export interface InvoiceLine_free_Props {
    line: InvoiceEntry2;
    onChange?: (id: string, patch: Partial<InvoiceEntry2>) => void;
    onDelete?: (id: string) => void;
    allowEdition: boolean;
    isNeomytic: boolean;
    NoVATDisplay: boolean;
}
export const InvoiceLine_free = ({ line, onChange, onDelete, allowEdition, isNeomytic, NoVATDisplay }: InvoiceLine_free_Props) => {
    const styles = useEditorStyles();
    const styles22 = useStyles();
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: line.id ?? 0 });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        flexGrow: 1
    };

    return (<div ref={setNodeRef} style={style} {...attributes} className={styles.ContentBoxBrandBackground2}>
        <div className={styles22.row}>
            <div {...listeners} style={{ cursor: isDragging ? "grabbing" : "grab" }}>
                <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                    <circle cx="3" cy="4" r="1.5" />
                    <circle cx="3" cy="10" r="1.5" />
                    <circle cx="3" cy="16" r="1.5" />
                    <circle cx="9" cy="4" r="1.5" />
                    <circle cx="9" cy="10" r="1.5" />
                    <circle cx="9" cy="16" r="1.5" />
                </svg>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Field label="Description" required >
                    <Input
                        autoComplete="off"
                        className={styles.fluid}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                            if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { description: data.value });
                        }}
                        value={line.description !== null ? line.description : ""}
                        maxLength={50}
                        disabled={!allowEdition}
                        style={!allowEdition ? {
                            color: tokens.colorNeutralForeground1, // text color 
                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                        } : {}}
                    />
                </Field>
                <Field label="Peppol Buyer Item Identification" >
                    <Input
                        autoComplete="off"
                        className={styles.fluid}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                            if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { buyerItemIdentification: data.value });
                        }}
                        value={line.buyerItemIdentification !== null ? line.buyerItemIdentification : ""}
                        maxLength={50}
                        disabled={!allowEdition}
                        style={!allowEdition ? {
                            color: tokens.colorNeutralForeground1, // text color 
                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                        } : {}}
                    />
                </Field>
                <Field label="Peppol Seller Item Identification" >
                    <Input
                        autoComplete="off"
                        className={styles.fluid}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                            if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { sellerItemIdentification: data.value });
                        }}
                        value={line.sellerItemIdentification !== null ? line.sellerItemIdentification : ""}
                        maxLength={50}
                        disabled={!allowEdition}
                        style={!allowEdition ? {
                            color: tokens.colorNeutralForeground1, // text color 
                            WebkitTextFillColor: tokens.colorNeutralForeground1, // required for Chrome
                        } : {}}
                    />
                </Field>
            </div>
            <Field label={t('Quantity')} className={styles.fluid}>
                <CurrencyInput
                    setCurrency={(count: number) => {
                        if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { count: count });
                    }}
                    currency={line.count}
                    digits={0}
                    disabled={!allowEdition}
                />
            </Field>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Field label={t('SellingPrice') + " (" + t('ExcludVAT') + ")"} className={styles.fluid} required>
                    <CurrencyInput
                        setCurrency={(price: number) => {
                            if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { sellPrice: price });
                        }}
                        currency={line.sellPrice}
                        digits={2}
                        contentAfter={"\u20AC"}
                        disabled={!allowEdition}
                    />
                </Field>
                {isNeomytic &&
                    <Field label={t('BuyingPrice') + " (" + t('ExcludVAT') + ")"} className={styles.fluid}>
                        <CurrencyInput
                            setCurrency={(price: number) => {
                                if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { sellPrice: price });
                            }}
                            currency={line.sellPrice}
                            digits={2}
                            contentAfter={"\u20AC"}
                            disabled={!allowEdition}
                        />
                    </Field>
                }
                {!NoVATDisplay &&
                    <Field label={t('VAT')} className={styles.fluid}>
                        <VATDropdown
                            currentVAT={line.vatcode}
                            setVatCode={(vat: number) => {
                                if (onChange && line.invoiceEntryid) onChange(line.invoiceEntryid, { vatcode: vat });
                            }}
                            disabled={!allowEdition}
                        />
                    </Field>
                }
            </div>
            <Button
                icon={<TrashCanIcon />}
                size="medium"
                shape="circular"
                aria-label={t('Delete')}
                title={t('Delete')}
                onClick={() => { if (onDelete && line.invoiceEntryid) onDelete(line.invoiceEntryid); }}
                disabled={!allowEdition}
            />
        </div>
    </div>
    );
};
